/**
 * Structured preview tables for accounting correction wizard (plain-language headers).
 */
import { useState } from 'react';
import AccountingChangeRecordsTable from './AccountingChangeRecordsTable';
import {
  wacStepDetailLabel,
  wacStepProductLabel,
  wacStepTankLabel,
  wacTypeLabel,
} from '../../../utils/dailyTransactionWacPreview';

function formatPurchasePrice(value) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatAmount(value) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function previewIntro(preview) {
  if (!preview?.fix_type) return null;
  const map = {
    opening_balance_reconcile: 'These opening balances will be updated to match what you entered on customer/bank screens.',
    nozzle_product_repair: 'These sales closing rows will get the correct petrol or diesel label.',
    nf_stock_reconcile: 'Shop stock counts will be adjusted as shown below.',
    nf_sale_gst_ledger_sync: 'Each row below shows the GST ledger fix for one shop/lubricant sale — current value, new value, and the formula applied.',
    wac_recalc_apply: 'Purchase cost and tank averages will be recalculated for the rows below.',
    wac_recalc_apply_sales: 'Purchase cost on fuel sales will be updated — tank purchase records stay the same.',
    wac_recalc_apply_purchase: 'Tank purchase averages will update — sales costs stay the same.',
    wac_rollback: 'Values will be restored to how they were before the selected recalculation.',
    gas_cylinder_ledger_repair: 'Each gas sale below lists ledger amounts that will be corrected to match the bill.',
    gas_exchange_owners_fund_align: 'Each exchange bill below lists P&L rows that will be added (old = not posted, new = amount added).',
    gas_exchange_purchase_owners_fund_align: 'Each exchange purchase below lists P&L debits that mirror empty-cylinder credits on the balance sheet.',
    cylinder_purchase_payment_status_align: 'Each row below is a cylinder purchase payment Cr that will change from ACTIVE to AC_AFTER_APR (included in Owners Fund).',
    gas_full_sale_owners_fund_align: 'Each full-cylinder sale below lists GST Adjustment debits to match balance-sheet GST credits.',
    fleet_card_owners_fund_align: 'Each fleet card sale below lists ledger rows to remove (inactive/wrong/duplicate) and repost on the active fleet card account.',
    ledger_trans_id_collision_split: 'Each fuel purchase below shares a transaction id with another entry. Apply gives the purchase a new id. Amounts stay the same. The other entry keeps the old id.',
    manual_review_employee_sx: 'Follow each step on the pump. Nothing is changed automatically.',
    manual_review_fuel_sales_stock: 'Follow each step to fix tank config or day entries. Nothing is changed automatically.',
    manual_review_owners_fund_mismatch: 'Follow each step to fix ledger postings on listed dates. Nothing is changed automatically.',
  };
  return map[preview.fix_type] || null;
}

function formatDisplayDate(value) {
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-');
    return `${d}-${m}-${y}`;
  }
  return value;
}

function WacPreviewSummaryBlock({ preview }) {
  const summary = preview?.summary;
  const stats = preview?.stats;
  if (!summary || typeof summary !== 'object') return null;

  const purchaseList = (summary.purchases || [])
    .map((p) => `${p.invoice_date || ''} #${p.unload_id || ''}`)
    .join(', ');

  const salesChanges = stats?.sales_rows_with_changes;
  const purchaseChanges = stats?.purchase_rows_with_changes;

  return (
    <div className="accounting-preview-summary small mb-2">
      {summary.date_range_adjusted_note && (
        <p className="text-info mb-1">{summary.date_range_adjusted_note}</p>
      )}
      <ul className="mb-2 ps-3">
        <li>
          <strong>Your selected from date:</strong>{' '}
          {summary.requested_from_date || summary.request_from_date || summary.from_date || '—'}
        </li>
        {(summary.requested_to_date || summary.resolved_to_date_display) && (
          <li>
            <strong>Your selected to date:</strong>{' '}
            {summary.requested_to_date || summary.to_date || '—'}
          </li>
        )}
        {(summary.resolved_from_date_display || summary.resolved_from_date) && (
          <li>
            <strong>Resolved scan from:</strong>{' '}
            {summary.resolved_from_date_display || formatDisplayDate(summary.resolved_from_date)}
            {' — '}
            <strong>through:</strong>{' '}
            {summary.resolved_to_date_display || formatDisplayDate(summary.resolved_to_date)}
          </li>
        )}
        <li>
          <strong>Anchor purchase (invoice date):</strong>{' '}
          {summary.anchor_purchase_date || '—'}
          {summary.anchor_unload_id ? ` (unload #${summary.anchor_unload_id})` : ''}
        </li>
        {summary.anchor_resolved_from_purchase && (
          <li className="text-info">
            Anchor is the last purchase on or before your from date — WAC uses purchase invoice date, not operation date.
          </li>
        )}
        <li><strong>Purchases in range:</strong> {summary.purchases_in_scope ?? '—'}</li>
        {purchaseList && <li><strong>Purchase dates:</strong> {purchaseList}</li>}
        <li>
          <strong>Correct sales/recoups from:</strong>{' '}
          {summary.sales_apply_from || summary.anchor_purchase_date || '—'}
        </li>
        <li>
          <strong>Correct sales/recoups through:</strong> {summary.apply_sales_through || summary.to_date || '—'}
        </li>
        {summary.formula && <li><strong>Formula:</strong> {summary.formula}</li>}
      </ul>
      {(salesChanges != null || purchaseChanges != null) && (
        <p className="mb-0">
          <strong>Sales changes:</strong> {salesChanges ?? '—'}
          {' | '}
          <strong>Purchase changes:</strong> {purchaseChanges ?? '—'}
        </p>
      )}
    </div>
  );
}

function PreviewSummaryBlock({ preview }) {
  const summary = preview?.summary;
  if (!summary || typeof summary !== 'object') return null;

  if (preview.fix_type?.startsWith('wac_recalc_apply')) {
    return <WacPreviewSummaryBlock preview={preview} />;
  }

  const hasReason = summary.reason || summary.detection;
  const hasFormula = summary.formula;

  if (!hasReason && !hasFormula) return null;

  return (
    <div className="accounting-preview-summary small mb-2">
      {hasReason && <p className="text-muted mb-1">{summary.reason}</p>}
      {summary.detection && (
        <p className="text-muted mb-1"><strong>Detection:</strong> {summary.detection}</p>
      )}
      {hasFormula && (
        <p className="mb-0"><strong>Formula applied:</strong> {summary.formula}</p>
      )}
    </div>
  );
}

/**
 * One non-fuel product row from the nf_stock_reconcile scan. Auto-fixable "issues" just display.
 * A "advisory" note (master total vs. the sales-statement ledger replay) is review-only by
 * design — it is never auto-applied — but a super-admin who has checked the real stock can type
 * the confirmed number here and send it back up as a reviewed `confirm_override`, which only
 * ever touches this one product_id once "Apply fix" is pressed below.
 */
function NfStockProductRow({ product: p, onConfirmOverride, stagedProductId }) {
  const hasAdvisory = (p.advisory || []).length > 0;
  const suggested = p.ledger_closing_stock != null ? Number(p.ledger_closing_stock).toFixed(2) : '';
  const [value, setValue] = useState(suggested);
  // Only one product's reviewed override can be staged at a time (confirm_override holds a
  // single product_id) — if a different row was staged after this one, this row's confirmed
  // state must clear so the UI never shows two rows as "staged" at once.
  const isStaged = stagedProductId === p.product_id;

  return (
    <tr key={p.product_id}>
      <td className="small">{p.product_name}</td>
      <td className="small">
        {(p.issues || []).join('; ') || (hasAdvisory ? '—' : 'No issue')}
        {hasAdvisory && (
          <div className="text-warning mt-1">{(p.advisory || []).join('; ')}</div>
        )}
      </td>
      <td>{p.auto_fixable ? 'Yes' : (hasAdvisory ? 'Needs reviewed value (below)' : 'Needs manual review')}</td>
      <td className="small">
        {hasAdvisory && onConfirmOverride ? (
          <div className="d-flex align-items-center gap-1">
            <input
              type="number"
              step="0.01"
              className="form-control form-control-sm"
              style={{ width: '110px' }}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Confirmed stock"
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-warning"
              disabled={value === '' || Number.isNaN(Number(value))}
              onClick={() => onConfirmOverride(p.product_id, Number(value))}
            >
              Use this value
            </button>
            {isStaged && (
              <span className="text-success small">
                Staged — re-run preview, then apply.
              </span>
            )}
          </div>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

const AccountingPreviewTable = ({ preview, onConfirmOverride }) => {
  const [stagedProductId, setStagedProductId] = useState(null);

  if (!preview) return null;

  function handleConfirmOverride(productId, targetTotalStock) {
    setStagedProductId(productId);
    onConfirmOverride?.(productId, targetTotalStock);
  }

  const intro = previewIntro(preview);

  if (preview.checklist?.length) {
    return (
      <>
        {intro && <p className="small text-muted mb-2">{intro}</p>}
        <ol className="accounting-playbook-list mt-2 mb-0">
          {preview.checklist.map((item) => (
            <li key={item.step || item.title} className="accounting-playbook-step">
              <strong>{item.title}</strong>
              {item.detail && <div className="small text-muted mt-1">{item.detail}</div>}
            </li>
          ))}
        </ol>
      </>
    );
  }

  if (preview.change_records?.length || (preview.change_records_total ?? 0) > 0) {
    return (
      <>
        {intro && <p className="small text-muted mb-2">{intro}</p>}
        <PreviewSummaryBlock preview={preview} />
        <AccountingChangeRecordsTable
          records={preview.change_records || []}
          total={preview.change_records_total ?? preview.change_records?.length ?? 0}
          truncated={preview.change_records_truncated}
          source="preview"
        />
      </>
    );
  }

  if (preview.fix_type === 'wac_rollback' || preview.summary?.baseline_audit_id) {
    return (
      <>
        {intro && <p className="small text-muted mb-2">{intro}</p>}
        <table className="table table-sm admin-pump-table mt-2">
          <tbody>
            <tr><th>Restore from saved point</th><td>#{preview.summary?.baseline_audit_id}</td></tr>
            <tr><th>Recalculations to undo</th><td>{preview.summary?.audits_to_undo ?? preview.rows_to_change}</td></tr>
            <tr><th>From date</th><td>{preview.summary?.from_date || '—'}</td></tr>
          </tbody>
        </table>
      </>
    );
  }

  if (preview.steps?.length) {
    const totalChanges = preview.stats?.rows_with_changes ?? preview.rows_to_change ?? preview.steps.length;
    const truncated = Boolean(preview.stats?.preview_steps_truncated) || totalChanges > preview.steps.length;

    return (
      <>
        {intro && <p className="small text-muted mb-2">{intro}</p>}
        <PreviewSummaryBlock preview={preview} />
        {truncated && (
          <p className="small text-muted mb-2">
            Showing {preview.steps.length} of {totalChanges} changed row(s). Apply still corrects every row in the date range.
          </p>
        )}
        <div className="table-responsive mt-2">
          <table className="table table-sm admin-pump-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Date</th>
                <th>Product Name</th>
                <th>Detail</th>
                <th>Tank</th>
                <th className="text-end">Qty (L)</th>
                <th className="text-end">Old PP</th>
                <th className="text-end">New PP</th>
                <th className="text-end">Old COGS</th>
                <th className="text-end">New COGS</th>
                <th>Calculation</th>
              </tr>
            </thead>
            <tbody>
              {preview.steps.map((step, i) => (
                <tr key={i} className={step.will_change ? 'table-warning' : undefined}>
                  <td>{wacTypeLabel(step.type || step.event_type)}</td>
                  <td>{step.date || step.op_date || '—'}</td>
                  <td className="small">{wacStepProductLabel(step)}</td>
                  <td className="small">{wacStepDetailLabel(step)}</td>
                  <td className="small">{wacStepTankLabel(step)}</td>
                  <td className="text-end">{step.quantity_lit != null ? formatAmount(step.quantity_lit) : '—'}</td>
                  <td className="text-end">{formatPurchasePrice(step.old_purchase_price ?? step.old_price)}</td>
                  <td className="text-end">{formatPurchasePrice(step.new_purchase_price ?? step.new_price)}</td>
                  <td className="text-end">{formatAmount(step.old_cogs)}</td>
                  <td className="text-end">{formatAmount(step.new_cogs)}</td>
                  <td className="small">{step.calculation || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  if (preview.sample_rows?.length) {
    const row = preview.sample_rows[0];
    if (row.master_amount != null || row.issue) {
      return (
        <>
          {intro && <p className="small text-muted mb-2">{intro}</p>}
          <div className="table-responsive mt-2">
            <table className="table table-sm admin-pump-table">
              <thead>
                <tr><th>Account</th><th>Problem</th><th>Amount on master screen</th><th>Amount in ledger</th></tr>
              </thead>
              <tbody>
                {preview.sample_rows.slice(0, 20).map((r, i) => (
                  <tr key={i}>
                    <td className="small">{r.account || r.ac_name}</td>
                    <td className="small">{r.issue}</td>
                    <td>{r.master_amount != null ? `₹${r.master_amount} ${r.master_debit_credit || ''}` : '—'}</td>
                    <td>{r.ledger_amount != null ? `₹${r.ledger_amount} ${r.ledger_debit_credit || ''}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }

    if (row.sale_id && row.updates?.length) {
      return (
        <>
          {intro && <p className="small text-muted mb-2">{intro}</p>}
          <PreviewSummaryBlock preview={preview} />
          <div className="table-responsive mt-2">
            <table className="table table-sm admin-pump-table">
              <thead>
                <tr>
                  <th>Sale</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Ledger field</th>
                  <th>Current</th>
                  <th>After fix</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample_rows.slice(0, 20).flatMap((r) => (
                  (r.updates || []).map((update, i) => (
                    <tr key={`${r.sale_id}-${i}`}>
                      <td>#{r.sale_id}</td>
                      <td>{r.op_date}</td>
                      <td>{r.sale_type}</td>
                      <td className="small">{update.field}</td>
                      <td className="small">
                        {update.old_amount != null ? `₹${update.old_amount}` : 'Current ledger value'}
                      </td>
                      <td className="small">₹{update.new_amount}</td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }

    if (row.trans_id && row.planned_entries?.length) {
      return (
        <>
          {intro && <p className="small text-muted mb-2">{intro}</p>}
          <PreviewSummaryBlock preview={preview} />
          <div className="table-responsive mt-2">
            <table className="table table-sm admin-pump-table">
              <thead>
                <tr>
                  <th>Transaction</th>
                  <th>Date</th>
                  <th>Owners Fund gap</th>
                  <th>Account</th>
                  <th>Side</th>
                  <th>Before</th>
                  <th>After</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample_rows.slice(0, 20).flatMap((r) => (
                  (r.planned_entries || []).map((entry, i) => (
                    <tr key={`${r.trans_id}-${i}`}>
                      <td>{r.trans_id}</td>
                      <td>{r.ac_date}</td>
                      <td>{entry.amount != null && r.owners_fund_gap != null ? `₹${r.owners_fund_gap}` : '—'}</td>
                      <td>{entry.account}</td>
                      <td>{entry.side}</td>
                      <td className="accounting-change-old">Not posted</td>
                      <td className="accounting-change-new">₹{entry.amount}</td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }

    if (row.tank_fuel || row.stored_product) {
      return (
        <>
          {intro && <p className="small text-muted mb-2">{intro}</p>}
          <div className="table-responsive mt-2">
            <table className="table table-sm admin-pump-table">
              <thead>
                <tr><th>Actual tank fuel</th><th>Saved as product</th><th>Transaction</th></tr>
              </thead>
              <tbody>
                {preview.sample_rows.slice(0, 20).map((r, i) => (
                  <tr key={i}>
                    <td>{r.tank_fuel}</td>
                    <td>{r.stored_product}</td>
                    <td>{r.trans_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }
  }

  if (preview.products?.length) {
    const anyAdvisory = preview.products.some((p) => (p.advisory || []).length > 0);
    return (
      <>
        {intro && <p className="small text-muted mb-2">{intro}</p>}
        {anyAdvisory && (
          <p className="small text-warning mb-2">
            Rows marked in orange are review-only — the sales-statement ledger does not match
            master stock, but this is never changed automatically. If you have checked the real
            stock (meta/FIFO ledger, physical count, or purchase history) and are confident in a
            number, type it in and click &quot;Use this value&quot;, then re-run preview above and
            apply the fix with a reason. Only that one product is changed.
          </p>
        )}
        <div className="table-responsive mt-2">
          <table className="table table-sm admin-pump-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>What is wrong</th>
                <th>Can auto-fix?</th>
                <th>Reviewed correction</th>
              </tr>
            </thead>
            <tbody>
              {preview.products.slice(0, 20).map((p) => (
                <NfStockProductRow
                  key={p.product_id}
                  product={p}
                  onConfirmOverride={onConfirmOverride ? handleConfirmOverride : undefined}
                  stagedProductId={stagedProductId}
                />
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  if (preview.summary && typeof preview.summary === 'object') {
    return (
      <>
        <PreviewSummaryBlock preview={preview} />
        <details className="mt-2">
          <summary className="small btn btn-link p-0">Show technical summary</summary>
          <pre className="audit-detail-pre mt-2">{JSON.stringify(preview.summary, null, 2)}</pre>
        </details>
      </>
    );
  }

  return null;
};

export default AccountingPreviewTable;
